'use client';
import { useQuery } from '@tanstack/react-query';
import { ListImportableOrderDocumentsResponseBody } from 'bff';
import { IconPaperclip } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  cn,
} from 'ui';
import { WretchError } from 'wretch/resolver';
import { ImportOrderDocumentsPicker } from './ImportOrderDocumentsPicker';

// order_document_import — sibling of UploadDocumentDialog for an EXISTING order:
// the fetcher and the import call are injected, so it stays app-agnostic.
export const ImportOrderDocumentsDialog = ({
  queryKey,
  fetcher,
  onImport,
  onError,
  maxDocuments,
  triggerLabel = 'Adjuntar del reclamo',
  title = 'Adjuntar documentación del reclamo',
  variant = 'link',
}: {
  // Identifies the record being listed. The search term is appended here.
  queryKey: unknown[];
  fetcher: (params: {
    search?: string;
  }) => Promise<ListImportableOrderDocumentsResponseBody>;
  onImport: (fileIds: string[]) => Promise<unknown>;
  onError: (error: WretchError) => unknown;
  maxDocuments: number;
  triggerLabel?: string;
  title?: string;
  variant?: 'default' | 'outline' | 'link';
}) => {
  const [open, setOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [search, setSearch] = useState<string>();
  const [selected, setSelected] = useState<string[]>([]);

  const query = useQuery({
    queryKey: [...queryKey, search],
    queryFn: () => fetcher({ search }),
    // Only fetched while the dialog is open, and the previous page is kept so
    // typing in the search box does not flash the skeletons on every keystroke.
    enabled: open,
    keepPreviousData: true,
    onError,
  });

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    setSelected([]);
    setSearch(undefined);
  }, []);

  const onSubmit = useCallback(() => {
    if (selected.length === 0) return;

    setIsImporting(true);
    onImport(selected)
      .then(() => handleOpenChange(false))
      .catch(onError)
      .finally(() => setIsImporting(false));
  }, [handleOpenChange, onError, onImport, selected]);

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogTrigger asChild>
        <Button
          size='sm'
          className={cn(
            'h-8 rounded-2xl shrink-0 whitespace-nowrap flex items-center',
            variant === 'outline' && 'border border-gray-200 hover:bg-gray-100',
            variant === 'link' && 'text-dark',
          )}
          variant={variant}
        >
          {variant === 'link' && <IconPaperclip className='h-4 w-4 mr-1' />}
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-4xl max-h-[80vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className='sr-only'>
            Seleccioná documentación existente para compartirla con el
            proveedor.
          </DialogDescription>
        </DialogHeader>

        <ImportOrderDocumentsPicker
          relationType={query.data?.relation_type}
          sections={query.data?.sections ?? []}
          truncated={query.data?.truncated}
          isLoading={query.isLoading}
          value={selected}
          onValueChange={setSelected}
          onSearchChange={setSearch}
          maxDocuments={maxDocuments}
        />

        <DialogFooter>
          <Button
            size='sm'
            onClick={onSubmit}
            loading={isImporting}
            disabled={selected.length === 0}
          >
            Adjuntar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
